import {
  STEP_NAME_TO_CODES,
  overrides,
  allowedItems,
} from './new-product-intro.constants';
import { selectors } from '../../state';
import {
  itemStatusesByKind,
  statusFromItems,
} from '../dashboard/dashboard.utils';
import { isFunction } from 'lodash-es';
import { productIntroCards as ENTRY_ID } from '../../state/contentful/entries.json';

export const resetReducer = (state, windowWidth) => {
  const cards = state && state.cards ? state.cards : [];
  // Fine tune the card width based on the viewport size.
  const slideWidth = windowWidth >= 1200 ? 400 : windowWidth >= 375 ? 336 : 272;
  const trackWidth = cards.length * slideWidth;
  const scrollable = windowWidth < trackWidth;

  let newState = {
    cards,
    slideWidth,
    trackWidth,
    scrollable,
  };

  const cardIndex = newState.scrollable ? 0 : 1;

  newState = {
    ...newState,
    ...enterCardReducer(newState, cardIndex),
  };

  return newState;
};

// When entering a card, one of three modes is applied:
// MOBILE: the user is entering a card when the carousel
// overflows the viewport and is scrollable and assumed
// to be on a mobile device.
// ACTIVE: the user is entering a card when the carousel
// fits within the viewport and is not scrollable and assumed
// to be on a desktop device.
// DEFAULT: the user has not entered the card
export const enterCardReducer = (state = {}, index) => {
  const noCards = !state.cards || state.cards.length <= 0;

  if (noCards) {
    return state;
  }

  const isMobile = state.scrollable;
  const isNotMobile = !state.scrollable;
  const enteredCardIsActive =
    (isMobile && state.cards[index].mode === 'MOBILE') ||
    (isNotMobile && state.cards[index].mode === 'ACTIVE');

  if (enteredCardIsActive) {
    return state;
  }

  return {
    ...state,
    cards: state.cards.map((card, i) => ({
      ...card,
      mode: index !== i ? 'DEFAULT' : state.scrollable ? 'MOBILE' : 'ACTIVE',
    })),
  };
};

export const leaveCardReducer = (state = {}) =>
  // If the carousel is scrollabe, we leave the previous entered
  // card in MOBILE mode. It only get's removed when we enter another.
  state.scrollable
    ? state
    : !state.cards
    ? state
    : {
        ...state,
        cards: state.cards.map(card => ({
          ...card,
          mode: 'DEFAULT',
        })),
      };

// When the user leaves the carousel, we select the second item. The carousel
// is assumed to hold 3 cards, so this will select the middle card. When
// scrollable, we are on a mobile device, and we want to leave the previously
// entered mode as is.
export const leaveContainerReducer = state =>
  state.scrollable ? state : { ...state, ...enterCardReducer(state, 1) };

export const byAlternateCardWeight = cards => (a, b) =>
  cards[b.code].alternateWeight - cards[a.code].alternateWeight;

export const byCardWeight = cards => (a, b) =>
  cards[b.code].weight - cards[a.code].weight;

const callPropFunctions = (state, overrides) => definition =>
  Object.keys(definition).reduce((def, key) => {
    const override =
      overrides[definition.code] && overrides[definition.code][key];
    return {
      ...def,
      [key]: isFunction(override) ? override(state, definition) : def[key],
    };
  }, definition);

export function extractCardsFromItemDefinitions(
  itemDefinitions,
  cardData,
  itemStatuses = [],
  state = {},
  overrides = {},
  useAlternateWeight = false
) {
  if (!itemDefinitions || itemDefinitions.length === 0) {
    return [];
  }

  const cards = itemDefinitions
    // Only process item definitions that exist in our constants
    .filter(definition => allowedItems.includes(definition.code))
    // Only process item definitions that exist in our Contentful data
    .filter(definition => Object.keys(cardData).includes(definition.code))
    .filter(definition => {
      const { status } = statusFromItems(itemStatuses[definition.code]);
      return (
        status !== 'completed' &&
        status !== 'pending' &&
        status !== 'reserved' &&
        status !== 'confirmed'
      );
    })
    .filter(definition => {
      const { exclusion = () => true } = cardData[definition.code];

      return exclusion(state);
    })
    .sort(
      useAlternateWeight
        ? byAlternateCardWeight(cardData)
        : byCardWeight(cardData)
    )
    .slice(0, 3)
    .map(definition => Object.assign({}, definition, cardData[definition.code]))
    .map(callPropFunctions(state, overrides));

  return cards;
}

export function processContentfulData(
  { fields: { references = [] } = {} } = {},
  overrides = {}
) {
  if (!references.length) {
    return null;
  }

  return references
    .filter(({ fields }) =>
      // Only process Contentful steps that exist in our constants
      Object.keys(STEP_NAME_TO_CODES).includes(fields.step)
    )
    .map(({ fields }) => ({
      ...fields,
      cta: {
        label: fields.ctaLabel,
        state: STEP_NAME_TO_CODES[fields.step].state,
      },
      exclusion:
        overrides[STEP_NAME_TO_CODES[fields.step].code] &&
        overrides[STEP_NAME_TO_CODES[fields.step].code].exclusion,
    }))
    .reduce(
      (cards, card) => ({
        ...cards,
        [STEP_NAME_TO_CODES[card.step].code]: card,
      }),
      {}
    );
}

export function mapStateToThis(state, useAlternateWeight = false) {
  const itemDefinitions = selectors.itemDefinition
    .all(state)
    .map(itemDefinition => ({
      code: itemDefinition.code(state),
      icon: itemDefinition.icon(state),
      page: itemDefinition.page(state),
    }));

  const cards = processContentfulData(
    selectors.contentful.getEntry(state, ENTRY_ID),
    overrides
  );

  if (!cards) {
    return { origCards: [], cards: [] };
  }

  const itemStatuses = itemStatusesByKind(state);
  const cardsToShow = extractCardsFromItemDefinitions(
    itemDefinitions,
    cards,
    itemStatuses,
    state,
    overrides,
    useAlternateWeight
  );

  return { origCards: cardsToShow, cards: cardsToShow };
}
